import { useCallback, useEffect, useMemo, useRef as useRefRR, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Calendar as CalendarIcon, Plus, Trash2, Upload, Grid3x3 } from "lucide-react";

const WEEKDAYS = [
  { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" },
  { v: 3, l: "Thu" }, { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];

export default function RateRulesPage() {
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [plots, setPlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRefRR(null);
  const [form, setForm] = useState({
    scope: "project", project_id: "", plot_id: "",
    name: "", start_date: "", end_date: "", nightly_rate: "",
    weekdays: [],
  });

  const load = useCallback(async () => {
    const [r, p, pl] = await Promise.all([
      api.get("/rate-rules"), api.get("/projects"), api.get("/plots"),
    ]);
    setRules(r.data); setProjects(p.data); setPlots(pl.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cottagePlots = useMemo(() => plots.filter((p) => p.has_cottage), [plots]);

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/rate-rules/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} rule(s) imported${data.errors.length ? ` · ${data.errors.length} error(s)` : ""}`);
      if (data.errors.length) toast.warning(`Imported with ${data.errors.length} row error(s)`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        start_date: form.start_date, end_date: form.end_date,
        nightly_rate: Number(form.nightly_rate),
      };
      if (form.scope === "project") payload.project_id = form.project_id;
      else payload.plot_id = form.plot_id;
      if (form.weekdays.length > 0 && form.weekdays.length < 7) payload.weekdays = form.weekdays;
      await api.post("/rate-rules", payload);
      toast.success("Rule saved");
      setOpen(false);
      setForm({ scope: "project", project_id: "", plot_id: "", name: "", start_date: "", end_date: "", nightly_rate: "", weekdays: [] });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rate rule?")) return;
    await api.delete(`/rate-rules/${id}`);
    load();
  };

  const projName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const plotName = (id) => {
    const p = plots.find((x) => x.id === id);
    return p ? `Plot ${p.plot_number}` : "—";
  };
  const fmt = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

  const toggleDay = (d) => {
    setForm((s) => ({
      ...s,
      weekdays: s.weekdays.includes(d) ? s.weekdays.filter((x) => x !== d) : [...s.weekdays, d].sort(),
    }));
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarIcon className="text-primary" />
          <h1 className="heading text-3xl md:text-4xl">Rate cards</h1>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={doImport} data-testid="rate-csv-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="rate-import-btn">
            <Upload size={14} className="mr-2" />{importing ? "Importing…" : "Import CSV"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-rate-btn"><Plus size={14} className="mr-2" />New rate</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New rate rule</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="rate-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">Whole project</SelectItem>
                    <SelectItem value="plot">Single cottage plot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope === "project" ? (
                <div>
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="rate-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Cottage plot</Label>
                  <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                    <SelectTrigger data-testid="rate-plot-select"><SelectValue placeholder="Select plot" /></SelectTrigger>
                    <SelectContent>{cottagePlots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number} · {projName(p.project_id)}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Season name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Peak (Diwali), Monsoon, Weekend" data-testid="rate-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rate-start-input" /></div>
                <div><Label>End</Label><Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="rate-end-input" /></div>
              </div>
              <div><Label>Nightly rate (₹)</Label><Input type="number" step="50" required min="0" value={form.nightly_rate} onChange={(e) => setForm({ ...form, nightly_rate: e.target.value })} data-testid="rate-amount-input" /></div>
              <div>
                <Label>Days of week (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {WEEKDAYS.map((d) => (
                    <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                      className={`text-xs uppercase tracking-widest px-2.5 py-1 rounded-md border ${form.weekdays.includes(d.v) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                      data-testid={`rate-day-${d.v}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Empty = every day. Pick Sat/Sun for a weekend-only peak.</p>
              </div>
              <DialogFooter><Button type="submit" data-testid="rate-save-btn">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-6">Seasonal or day-of-week rate overrides. Plot rules win over project rules. When a night is not covered, the plot or project default rate applies. <br /><span className="text-[11px]">CSV columns: <code>project,plot,name,start_date,end_date,nightly_rate,weekdays</code> (weekdays pipe-separated 0..6, e.g. <code>5|6</code> for weekend). Provide either project OR plot.</span></p>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {rules.length === 0 && <div className="p-6 text-sm text-muted-foreground">No rate rules yet.</div>}
        {rules.map((r) => (
          <div key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`rate-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{r.name} <span className="text-primary">· {fmt(r.nightly_rate)}/night</span></div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                {r.plot_id ? plotName(r.plot_id) : projName(r.project_id)} · {r.start_date} → {r.end_date}
                {r.weekdays && r.weekdays.length > 0 && r.weekdays.length < 7 && ` · ${r.weekdays.map((d) => WEEKDAYS[d].l).join(" ")}`}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`rate-del-${r.id}`}><Trash2 size={12} /></Button>
          </div>
        ))}
      </div>

      <RateCalendar projects={projects} />
    </div>
  );
}

function RateCalendar({ projects }) {
  const now = new Date();
  const [projectId, setProjectId] = useState("");
  const [plotId, setPlotId] = useState("");
  const [plots, setPlots] = useState([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (projects.length && !projectId) setProjectId(projects[0].id);
  }, [projects, projectId]);

  useEffect(() => {
    if (!projectId) { setPlots([]); return; }
    api.get(`/plots?project_id=${projectId}`).then((r) => setPlots(r.data.filter((p) => p.has_cottage)));
    setPlotId("");
  }, [projectId]);

  useEffect(() => {
    if (!projectId && !plotId) return;
    const qs = plotId ? `plot_id=${plotId}` : `project_id=${projectId}`;
    api.get(`/rate-calendar?${qs}&year=${year}&month=${month}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [projectId, plotId, year, month]);

  const fmt = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
  const days = data?.days || [];
  const rates = days.map((d) => d.rate).filter((r) => r > 0);
  const minR = rates.length ? Math.min(...rates) : 0;
  const maxR = rates.length ? Math.max(...rates) : 1;
  const colour = (rate) => {
    if (!rate || minR === maxR) return "hsl(var(--secondary))";
    const t = (rate - minR) / (maxR - minR);
    // Green → orange gradient
    const h = 144 - t * 120;
    return `hsl(${h}, 50%, ${90 - t * 20}%)`;
  };

  const first = days[0] ? new Date(days[0].date) : new Date(year, month - 1, 1);
  const startBlanks = (first.getDay() + 6) % 7; // Monday-start

  return (
    <div className="mt-10 p-6 border border-border rounded-lg bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Grid3x3 size={16} className="text-primary" />
          <div>
            <div className="heading text-xl">Rate calendar</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Effective nightly rate per day</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-56" data-testid="cal-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={plotId || "__all__"} onValueChange={(v) => setPlotId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="cal-plot-select"><SelectValue placeholder="All cottages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All cottages</SelectItem>
              {plots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input type="number" value={year} min="2024" max="2100" onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="cal-year-input" />
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-28" data-testid="cal-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
              <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-2" data-testid="rate-calendar-grid">
        {Array.from({ length: startBlanks }).map((_, i) => <div key={`b${i}`} />)}
        {days.map((d) => (
          <div key={d.date} className="p-2 rounded-md border border-border text-xs relative"
               style={{ background: colour(d.rate) }} data-testid={`cal-day-${d.date}`}>
            <div className="font-semibold">{new Date(d.date).getDate()}</div>
            <div className="mt-1 text-[11px]">{fmt(d.rate)}</div>
            {d.rule && <div className="text-[9px] uppercase tracking-wider mt-0.5 truncate">{d.rule}</div>}
          </div>
        ))}
      </div>
      {data && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
          Default {fmt(data.default_rate)} · Range {fmt(minR)} – {fmt(maxR)}
        </div>
      )}
    </div>
  );
}
